import {
  SimpleModal,
  SimpleModalHeader,
  SimpleModalTitle,
} from "../../components/ui/simple-modal";
import { UserAvatar } from "../../components/ui/avatar";
import { UserIdentity } from "../../components/UserIdentity";
import { useMemo } from "react";
import { useAuth } from "../../app/useAuth";
import { useSharedProfileData } from "../../hooks/useSharedProfileData";
import { useEnrichedSchedulers } from "../../hooks/useEnrichedSchedulers";
import { useSharedBasicPolls } from "../../hooks/useSharedBasicPolls";
import { useUserProfiles } from "../../hooks/useUserProfiles";
import { LoadingState } from "../../components/ui/spinner";
import { SharedProfileContent } from "./SharedProfileContent";

/**
 * Modal that displays a friend's profile with shared polls, groups, and mutual friends.
 *
 * Props:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void
 * - user: { email, displayName, avatar, ... }
 */
export function FriendProfileModal({ open, onOpenChange, user, onFriendClick }) {
  const { user: currentUser } = useAuth();
  const email = user?.email;
  const { sharedPolls, sharedGroups, mutualFriends, getGroupColor, groupsById, loading } =
    useSharedProfileData(email);
  const { enrichUsers } = useUserProfiles(mutualFriends);
  const mutualFriendProfiles = enrichUsers(mutualFriends);
  const { enrichedSchedulers, loading: enrichLoading } =
    useEnrichedSchedulers(sharedPolls, currentUser?.uid);
  const sharedGroupIds = useMemo(
    () => sharedGroups.map((g) => g.id),
    [sharedGroups]
  );
  const { sharedBasicPolls, loading: basicPollsLoading } =
    useSharedBasicPolls({ sharedGroupIds, groupsById, getGroupColor });

  if (!user) return null;

  const isLoading = loading || enrichLoading || basicPollsLoading;

  return (
    <SimpleModal open={open} onOpenChange={onOpenChange} contentClassName="max-w-xl">
      <div>
        <SimpleModalHeader>
          <div className="flex items-center gap-3">
            <UserAvatar user={user} email={email} src={user.avatar} size={48} />
            <div className="min-w-0">
              <SimpleModalTitle>
                <UserIdentity user={user} />
              </SimpleModalTitle>
            </div>
          </div>
        </SimpleModalHeader>

        {isLoading ? (
          <div className="mt-6">
            <LoadingState message="Loading profile..." />
          </div>
        ) : (
          <SharedProfileContent
            sharedPolls={enrichedSchedulers}
            sharedGroups={sharedGroups}
            sharedBasicPolls={sharedBasicPolls}
            mutualFriendProfiles={mutualFriendProfiles}
            getGroupColor={getGroupColor}
            groupsById={groupsById}
            onFriendClick={onFriendClick}
          />
        )}
      </div>
    </SimpleModal>
  );
}
